/* eslint-disable max-len */
import * as React from 'react';
import Link from 'next/link';
import { WebcamType } from '@/api/region/types';
import { cn } from '@/util/cn';
import { Text } from '@/components/ui/text';
import { LiveWebcamStream } from '@/components/common/ResortWebcam/LiveStream';

// Live-cam card used across resort + region surfaces. One shape:
//   - rounded photo
//   - "X mins ago" timestamp pill anchored bottom-right
//   - caption / resort-name pair below the photo
//
// When `videoStream` is set the photo area hosts a consent-gated live
// iframe (see ./LiveStream.tsx) instead of the still: the image stays
// underneath as a poster, the timestamp pill is dropped (it describes
// the still capture, not the stream), and `href` / `onClick` are ignored
// so clicks reach the player.
//
// The `showResort` flag flips the label hierarchy depending on where
// the cam is rendered:
//
//   showResort=false  (default — on a resort-level page)
//     → caption is the bold primary label
//       (the resort context is already established by the page)
//
//   showResort=true   (on a region / multi-resort surface)
//     → resort name is the bold primary label
//     → caption renders below as muted secondary
//
// Wraps in <Link> when `href` is provided.
//
// Production peer: `src/components/common/WebcamItem/` (4 callers).
// The peer doesn't carry `badge` or `showResort` — adopters either
// extend the peer or switch to this component during retrofit.

const ASPECTS = {
  video: 'aspect-video',
  square: 'aspect-square',
  '4/3': 'aspect-[4/3]',
} as const;

export type ResortWebcamProps = {
  /** Location / cam name. Always provided. e.g. "Top of Gondola", "Mid-Vail". */
  caption: string;
  /** Resort the cam belongs to. Required when `showResort` is true. */
  resortName?: string;
  /** When true, render `resortName` as the primary label and `caption` as secondary. */
  showResort?: boolean;
  image: string;
  /**
   * Live-stream URL. When set, a consent-gated iframe renders over the
   * photo (which stays as the poster) and `href` / `timestamp` are ignored.
   */
  videoStream?: string;
  /** Stream provider; Feratel streams get an extra `lg` language param. */
  webcamType?: WebcamType;
  /** Relative timestamp shown bottom-right of the photo. Omit to hide. */
  timestamp?: string;
  /** Optional badge content rendered top-left of the photo (e.g. fresh snow pill). */
  badge?: React.ReactNode;
  /** Wraps the card in next/link when set. Takes precedence over `onClick`. */
  href?: string;
  /**
   * Renders the card as a <button> when set (and `href` / `videoStream`
   * are absent) — image and caption become one click target.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Aspect ratio of the photo. Defaults to 'video' (16:9). */
  aspect?: keyof typeof ASPECTS;
  className?: string;
};

const TimestampPill = ({ children }: { children: React.ReactNode }) => (
  <span className="absolute bottom-3 right-3 rounded bg-black/65 px-2 py-0.5 text-xs font-medium text-white">
    {children}
  </span>
);

export const ResortWebcam = ({
  caption,
  resortName,
  showResort = false,
  image,
  videoStream,
  webcamType,
  timestamp,
  badge,
  href,
  onClick,
  aspect = 'video',
  className,
}: ResortWebcamProps) => {
  // Photo alt: lead with the resort name when we know it (regional
  // contexts where one card can reference any of dozens of resorts), so
  // image search picks up the resort-level association. Fall back to
  // the caption (cam location) when no resort name is supplied.
  const photoAlt = resortName ? `${resortName} — ${caption}` : caption;

  const photo = (
    <div className={cn('relative overflow-hidden rounded-md bg-muted', ASPECTS[aspect])}>
      <img
        src={image}
        alt={photoAlt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
      />
      {videoStream && (
        <LiveWebcamStream
          videoStream={videoStream}
          webcamType={webcamType}
          title={photoAlt}
        />
      )}
      {badge && (
        <span className="pointer-events-none absolute left-3 top-3">{badge}</span>
      )}
      {timestamp && !videoStream && <TimestampPill>{timestamp}</TimestampPill>}
    </div>
  );

  const captionBlock = showResort && resortName ? (
    <div className="mt-2">
      <div className="text-base font-bold leading-tight text-foreground group-hover:text-primary">
        {resortName}
      </div>
      <Text variant="subtle" className="mt-0.5 truncate text-xs text-muted-foreground">
        {caption}
      </Text>
    </div>
  ) : (
    <div className="mt-2">
      <div className="text-base font-bold leading-tight text-foreground group-hover:text-primary">
        {caption}
      </div>
    </div>
  );

  // A live card never wraps in a link: clicks must reach the embedded
  // player, not navigate away (parity with the legacy FeaturedCamera).
  if (href && !videoStream) {
    return (
      <Link href={href} className={cn('group block', className)}>
        {photo}
        {captionBlock}
      </Link>
    );
  }

  if (onClick && !videoStream) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn('group block w-full text-left', 'hover:[@media(hover:hover)]:cursor-zoom-in', className)}
      >
        {photo}
        {captionBlock}
      </button>
    );
  }

  return (
    <div className={cn('group block', className)}>
      {photo}
      {captionBlock}
    </div>
  );
};
